import InDocDiagramFrame from './InDocDiagramFrame';
import { inDocPalette, inDocType } from './inDocPalette';
import {
  InDocDefs,
  InDocGrid,
  InDocSectionLabel,
  InDocPanel,
} from './inDocDiagramKit';

const PREFIX = 'indocLoop';
const CX = 248;
const CY = 188;
const R = 70;
const loopPath = `M ${CX} ${CY - R} A ${R} ${R} 0 1 1 ${CX - 0.01} ${CY - R}`;

const loopNodes = [
  { id: 'plan', label: 'PLAN', x: CX, y: CY - R },
  {
    id: 'act',
    label: 'ACT',
    x: CX + R * Math.cos(Math.PI / 6),
    y: CY + R * Math.sin(Math.PI / 6),
  },
  {
    id: 'observe',
    label: 'OBSERVE',
    x: CX + R * Math.cos((5 * Math.PI) / 6),
    y: CY + R * Math.sin((5 * Math.PI) / 6),
  },
];

const tools = [
  { code: 'LIST', name: 'Survey Scope' },
  { code: 'SEARCH', name: 'Hybrid Retrieve' },
  { code: 'READ', name: 'Extract Fact' },
  { code: 'SUM', name: 'Condense' },
  { code: 'CMP', name: 'Diff Sources' },
  { code: 'FINISH', name: 'Cite And Close' },
];

const InDocInvestigationDiagram = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const c = inDocPalette(isDark);

  return (
    <InDocDiagramFrame
      theme={theme}
      caption="Plan, Act, Observe — Until The Answer Or The Budget"
      className="indoc-loop-frame"
    >
      <svg
        viewBox="0 0 720 400"
        className="w-full h-auto indoc-loop-svg"
        role="img"
        aria-label="inDoc investigation loop: the model plans, acts, and observes until the brief can name its sources or the step budget runs out"
      >
        <InDocDefs prefix={PREFIX} isDark={isDark} />
        <rect width="720" height="400" fill={c.bg} />
        <InDocGrid width={720} height={400} c={c} />

        <InDocSectionLabel x={360} y={36} c={c}>
          THE MODEL PICKS THE NEXT MOVE
        </InDocSectionLabel>

        <rect
          x="48"
          y="64"
          width="400"
          height="248"
          rx="8"
          fill={`url(#${PREFIX}-panel)`}
          stroke={c.panelStroke}
          strokeWidth="1"
        />

        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke={c.panelStroke}
          strokeWidth="1.5"
          strokeDasharray="6 8"
          className="indoc-loop-orbit"
        />
        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke={`url(#${PREFIX}-bus)`}
          strokeWidth="2.5"
          strokeLinecap="round"
          className="indoc-loop-flow"
        />
        <circle
          r="6"
          fill={c.sky}
          filter={`url(#${PREFIX}-glow)`}
          className="indoc-loop-token"
        >
          <animateMotion dur="6s" repeatCount="indefinite" path={loopPath} />
        </circle>

        {loopNodes.map(({ id, label, x, y }, i) => (
          <g key={id} className={`indoc-loop-node indoc-loop-node-${i}`}>
            <circle
              cx={x}
              cy={y}
              r="32"
              fill={c.panel}
              stroke={i === 0 ? c.sky : c.indigo}
              strokeWidth="1.5"
            />
            <text
              x={x}
              y={y + 4}
              textAnchor="middle"
              fill={c.label}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fontSize="8"
              fontWeight="600"
              letterSpacing="0.06em"
            >
              {label}
            </text>
          </g>
        ))}

        <circle
          cx={CX}
          cy={CY}
          r="24"
          fill={isDark ? '#0c1424' : '#e0f2fe'}
          stroke={`url(#${PREFIX}-core)`}
          strokeWidth="1.5"
          className="indoc-loop-core"
        />
        <text
          x={CX}
          y={CY + 4}
          textAnchor="middle"
          fill={c.accent}
          fontSize="9"
          fontWeight="800"
          letterSpacing="1"
        >
          inDoc
        </text>

        <InDocPanel x={468} y={64} w={204} h={248} prefix={PREFIX} c={c} active>
          <text x={478} y={86} fill={c.muted} {...inDocType.section}>
            TOOL CATALOG
          </text>
          {tools.map(({ code, name }, i) => (
            <g key={code} className={`indoc-loop-tool indoc-loop-tool-${i}`}>
              <rect
                x={480}
                y={100 + i * 32}
                width="180"
                height="26"
                rx="4"
                fill="none"
                stroke={c.panelStroke}
                strokeWidth="1"
                className="indoc-loop-tool-row"
              />
              <text
                x={492}
                y={117 + i * 32}
                fill={c.mono}
                {...inDocType.monoSm}
              >
                {code}
              </text>
              <text x={548} y={117 + i * 32} fill={c.label} {...inDocType.sub}>
                {name}
              </text>
            </g>
          ))}
        </InDocPanel>

        <rect
          x="48"
          y="328"
          width="624"
          height="48"
          rx="8"
          fill={`url(#${PREFIX}-panel)`}
          stroke={c.panelStroke}
          strokeWidth="1"
        />
        <text x={64} y={348} fill={c.muted} {...inDocType.section}>
          STEP BUDGET
        </text>
        <rect
          x={64}
          y={356}
          width="400"
          height="8"
          rx="4"
          fill={c.grid}
          opacity="0.45"
        />
        <rect
          x={64}
          y={356}
          width="400"
          height="8"
          rx="4"
          fill={c.sky}
          className="indoc-loop-budget"
        />
        <text x={480} y={364} fill={c.mono} {...inDocType.monoSm}>
          Exhausted → Finish Or Stop
        </text>
      </svg>
    </InDocDiagramFrame>
  );
};

export default InDocInvestigationDiagram;
